// src/pages/certificate-approvals/index.jsx
//
// Bandeja del JEFE DE LABORATORIO: calibraciones ya calculadas por el técnico
// esperando aprobación. Al aprobar se emite el certificado, se sube a la
// carpeta del cliente y el instrumento queda en "Revisado". Une eléctrica y
// presión en una sola lista.

import React, { useCallback, useEffect, useState } from 'react';
import { useLabPermissions } from '../../hooks/useLabPermissions';
import CertLabelModal from '../../components/CertLabelModal';
import {
  listPendingApprovals,
  approveCertificate,
  rejectCertificate,
  previewCertificate,
} from '../../utils/api-certificates';

const FAMILY_LABEL = { electrical: 'Eléctrica', pressure: 'Presión', external: 'Externo (subido)' };
FAMILY_LABEL.temperature = 'Temperatura';
FAMILY_LABEL.flow = 'Caudal';
FAMILY_LABEL.dimensional = 'Dimensional';
FAMILY_LABEL.mass = 'Masa';
FAMILY_LABEL.force = 'Fuerza / Torque';
FAMILY_LABEL.gas = 'Gases';
FAMILY_LABEL.valve = 'Válvulas';
FAMILY_LABEL.environmental = 'SSHH / Ambiental';
FAMILY_LABEL.analytical = 'Analítica';
const FAMILY_STYLE = {
  electrical: 'border-amber-500/40 bg-amber-500/10 text-amber-200',
  pressure: 'border-sky-500/40 bg-sky-500/10 text-sky-200',
  temperature: 'border-orange-500/40 bg-orange-500/10 text-orange-200',
  flow: 'border-cyan-500/40 bg-cyan-500/10 text-cyan-200',
  dimensional: 'border-indigo-500/40 bg-indigo-500/10 text-indigo-200',
  mass: 'border-fuchsia-500/40 bg-fuchsia-500/10 text-fuchsia-200',
  force: 'border-amber-500/40 bg-amber-500/10 text-amber-200',
  gas: 'border-emerald-500/40 bg-emerald-500/10 text-emerald-200',
  valve: 'border-cyan-500/40 bg-cyan-500/10 text-cyan-200',
  environmental: 'border-teal-500/40 bg-teal-500/10 text-teal-200',
  analytical: 'border-violet-500/40 bg-violet-500/10 text-violet-200',
  external: 'border-violet-500/40 bg-violet-500/10 text-violet-200',
};

function fmtDate(v) {
  if (!v) return '—';
  const s = String(v).slice(0, 10);
  const [y, m, d] = s.split('-');
  return y && m && d ? `${d}/${m}/${y}` : '—';
}

function rowKey(r) {
  return `${r.source_table}:${r.session_id}`;
}

export default function CertificateApprovalsPage() {
  const { role, isLabManager, loading: roleLoading } = useLabPermissions();
  const [rows, setRows] = useState(null);
  const [busyKey, setBusyKey] = useState(null);
  const [notice, setNotice] = useState(null);
  const [rejecting, setRejecting] = useState(null); // row en rechazo
  const [reason, setReason] = useState('');
  const [labelFor, setLabelFor] = useState(null); // { cert, instrument } recién aprobado

  const load = useCallback(async () => {
    try {
      setRows(await listPendingApprovals());
    } catch (e) {
      console.error('[Aprobaciones] load', e);
      setNotice({ type: 'error', text: 'No se pudo cargar la bandeja.' });
      setRows([]);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handlePreview = async (r) => {
    try {
      setBusyKey(rowKey(r));
      const { url } = await previewCertificate(r);
      window.open(url, '_blank', 'noopener,noreferrer');
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (e) {
      console.error('[Aprobaciones] preview', e);
      setNotice({ type: 'error', text: `No se pudo generar la vista previa: ${e?.message || e}` });
    } finally {
      setBusyKey(null);
    }
  };

  const handleApprove = async (r) => {
    const nombre = r.instrument?.lab_id ? `#${r.instrument.lab_id}` : (r.instrument_tag || 'este instrumento');
    if (!confirm(`¿Aprobar y emitir el certificado de ${nombre}?\n\nSe genera el PDF, se sube a la carpeta del cliente y el instrumento queda en "Revisado".`)) return;
    try {
      setBusyKey(rowKey(r));
      const cert = await approveCertificate(r);
      setNotice({ type: 'success', text: `Certificado ${cert.certificate_number} emitido y guardado en la carpeta del cliente.` });
      // Recién emitido: es el momento de imprimir el sticker.
      setLabelFor({ cert, instrument: r.instrument });
      await load();
    } catch (e) {
      console.error('[Aprobaciones] approve', e);
      setNotice({ type: 'error', text: `No se pudo aprobar: ${e?.message || e}` });
    } finally {
      setBusyKey(null);
    }
  };

  const handleReject = async () => {
    if (!rejecting) return;
    try {
      setBusyKey(rowKey(rejecting));
      await rejectCertificate(rejecting, reason);
      setNotice({ type: 'success', text: 'Devuelto al técnico con el motivo indicado.' });
      setRejecting(null);
      setReason('');
      await load();
    } catch (e) {
      console.error('[Aprobaciones] reject', e);
      setNotice({ type: 'error', text: `No se pudo rechazar: ${e?.message || e}` });
    } finally {
      setBusyKey(null);
    }
  };

  if (roleLoading) return <div className="p-6 text-slate-300">Cargando…</div>;

  return (
    <div className="p-6 space-y-5 max-w-[1400px] mx-auto">
      <div>
        <h1 className="text-2xl font-semibold text-white">Certificados pendientes de aprobación</h1>
        <p className="text-sm text-slate-400 mt-1">
          Calibraciones ya calculadas por el técnico. Al aprobar se emite el certificado, se guarda en la
          carpeta del cliente y el instrumento pasa a <b className="text-slate-200">Revisado</b>.
        </p>
      </div>

      {!isLabManager && (
        <div className="rounded-lg border border-amber-500/30 bg-amber-500/10 px-4 py-3 text-sm text-amber-200">
          Solo el <b>jefe de laboratorio o un administrador</b> puede aprobar certificados. Tu rol es <b>{role || 'sin rol'}</b>, así que
          esta bandeja es de solo lectura.
        </div>
      )}

      {notice && (
        <div
          className={`rounded-lg border px-4 py-3 text-sm flex items-start justify-between gap-3 ${
            notice.type === 'success'
              ? 'border-emerald-500/30 bg-emerald-500/10 text-emerald-200'
              : 'border-rose-500/30 bg-rose-500/10 text-rose-200'
          }`}
        >
          <span>{notice.text}</span>
          <button onClick={() => setNotice(null)} className="shrink-0 opacity-70 hover:opacity-100">✕</button>
        </div>
      )}

      {rows === null ? (
        <div className="text-sm text-slate-400">Cargando pendientes…</div>
      ) : rows.length === 0 ? (
        <div className="rounded-xl border border-white/10 bg-white/5 px-5 py-8 text-center text-slate-400">
          No hay certificados esperando aprobación. 🎉
        </div>
      ) : (
        <div className="space-y-3">
          {rows.map((r) => {
            const busy = busyKey === rowKey(r);
            const inst = r.instrument;
            const nombre = [inst?.brand || r.instrument_brand, inst?.model || r.instrument_model]
              .filter(Boolean).join(' ') || r.instrument_object || 'Instrumento';
            return (
              <div key={rowKey(r)} className="rounded-xl border border-white/10 bg-white/[0.03] p-4">
                <div className="flex items-start justify-between gap-4 flex-wrap">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-base font-bold text-white">
                        ID #{inst?.lab_id ?? '—'}
                      </span>
                      <span className={`text-[11px] px-2 py-0.5 rounded-full border ${FAMILY_STYLE[r.family] || ''}`}>
                        {FAMILY_LABEL[r.family] || r.family}
                      </span>
                      {(inst?.tag || r.instrument_tag) && (
                        <span className="text-[11px] bg-white/10 px-2 py-0.5 rounded-full text-slate-300">
                          TAG: {inst?.tag || r.instrument_tag}
                        </span>
                      )}
                    </div>
                    <div className="text-sm text-slate-200 mt-0.5 font-medium">{nombre}</div>
                    <div className="flex flex-wrap gap-x-5 gap-y-1 text-xs mt-1.5">
                      <span><span className="text-slate-500">Cliente: </span><span className="text-slate-200">{inst?.client?.name || r.client_name || '—'}</span></span>
                      <span><span className="text-slate-500">Serie: </span><span className="text-slate-300">{inst?.serial || r.instrument_serial || '—'}</span></span>
                      {r.service_order_code && (
                        <span><span className="text-slate-500">OS: </span><span className="text-slate-200">{r.service_order_code}</span></span>
                      )}
                      <span><span className="text-slate-500">Calibrado: </span><span className="text-slate-300">{fmtDate(r.calibration_date)}</span></span>
                      <span><span className="text-slate-500">Vence: </span><span className="text-slate-300">{fmtDate(r.valid_until)}</span></span>
                    </div>
                    {r.review_notes && (
                      <div className="mt-2 rounded-md border border-amber-500/20 bg-amber-500/10 px-2.5 py-1.5 text-xs text-amber-200">
                        <b>Rechazo anterior:</b> {r.review_notes}
                      </div>
                    )}
                  </div>

                  <div className="shrink-0 flex items-center gap-2 flex-wrap">
                    <button
                      onClick={() => handlePreview(r)}
                      disabled={busy}
                      className="px-3 py-2 rounded-lg border border-white/15 bg-white/5 text-sm text-slate-200 hover:border-white/30 transition disabled:opacity-60"
                    >
                      {busy ? 'Generando…' : '👁 Ver certificado'}
                    </button>
                    {isLabManager && (
                      <>
                        <button
                          onClick={() => { setRejecting(r); setReason(''); }}
                          disabled={busy}
                          className="px-3 py-2 rounded-lg border border-rose-500/40 bg-rose-500/10 text-sm text-rose-200 hover:bg-rose-500/20 transition disabled:opacity-60"
                        >
                          Rechazar
                        </button>
                        <button
                          onClick={() => handleApprove(r)}
                          disabled={busy}
                          className="px-4 py-2 rounded-lg bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-500 transition disabled:opacity-60"
                        >
                          {busy ? 'Emitiendo…' : '✓ Aprobar y emitir'}
                        </button>
                      </>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Etiqueta del certificado recién emitido */}
      <CertLabelModal
        open={!!labelFor}
        certificate={labelFor?.cert}
        instrument={labelFor?.instrument}
        onClose={() => setLabelFor(null)}
      />

      {/* Modal de rechazo */}
      {rejecting && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={(e) => { if (e.target === e.currentTarget) setRejecting(null); }}>
          <div className="w-full max-w-lg rounded-2xl border border-white/10 bg-[#12161a] p-5 space-y-3">
            <h3 className="text-lg font-semibold text-white">Rechazar y devolver al técnico</h3>
            <p className="text-sm text-slate-400">
              El instrumento vuelve a <b className="text-slate-200">En proceso</b> y el técnico ve tu motivo. No se emite certificado.
            </p>
            <textarea
              autoFocus
              rows={4}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Motivo del rechazo (obligatorio). Ej: falta el punto de 300 V; revisar la resolución cargada en resistencia."
              className="w-full rounded-lg bg-slate-800/60 border border-white/10 px-3 py-2 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:border-rose-500/50"
            />
            <div className="flex justify-end gap-2">
              <button onClick={() => setRejecting(null)} className="px-3 py-2 rounded-lg border border-white/15 text-sm text-slate-300 hover:bg-white/5">
                Cancelar
              </button>
              <button
                onClick={handleReject}
                disabled={!reason.trim() || busyKey === rowKey(rejecting)}
                className="px-4 py-2 rounded-lg bg-rose-600 text-white text-sm font-semibold hover:bg-rose-500 disabled:opacity-50"
              >
                Rechazar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
